import React from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import { FilePlayer } from 'react-player/lib/players/FilePlayer';
import {
  IChannelExpress,
  IJoinChannelCallback,
  IPhenix,
  ISubscriberCallback,
} from '../models/IPhenix';
import { getSDK } from '../utils';

const PHENIX_SDK_URL = 'https://unpkg.com/phenix-web-sdk@2019.2.3/dist/phenix-web-sdk.min.js';
const PHENIX_SDK_GLOBAL = 'phenix-web-sdk';

// TODO: Add optional auth data parameter at the end
const PHENIX_URL_REGEX = /^(.+?)\|(.+?)(?:\|(.+?))?$/i; // i hate this so much

function getPhenixSdk() {
  return getSDK<IPhenix>(PHENIX_SDK_URL, PHENIX_SDK_GLOBAL);
}

export class PhenixPlayer extends ReactPlayer {
  static displayName = 'PhenixPlayer';
  channelExpress: null | IChannelExpress = null;
  player: null | FilePlayer = null;
  static canEnablePIP() { return false; }

  componentDidMount() {
    this.load(this.props.url as string);
  }

  componentDidUpdate(prevProps: ReactPlayerProps) {
    if (prevProps.url !== this.props.url) {
      this.load(this.props.url as string);
    }
  }

  componentWillUnmount() {
    if (this.channelExpress) {
      this.channelExpress.dispose();
      this.channelExpress = null;
    }
  }

  getCurrentTime(): number {
    if (!this.player) { return 0; }
    return this.player.getCurrentTime();
  }
  getDuration(): number {
    if (!this.player) { return 0; }
    return this.player.getDuration();
  }

  getPhenixBackendUri(url = this.props.url): string {
    if (!(url && typeof url === 'string')) { return ''; }
    const match = PHENIX_URL_REGEX.exec(url);
    return match && match[1] ? match[1] : '';
  }

  getPhenixChannelId(url = this.props.url): string {
    if (!(url && typeof url === 'string')) { return ''; }
    const match = PHENIX_URL_REGEX.exec(url);
    return match && match[2] ? match[2] : '';
  }

  getSecondsLoaded = (): null | number => {
    if (!this.player) { return null; }
    return this.player.getSecondsLoaded();
  }

  load(url: string) {
    const backendUri = this.getPhenixBackendUri(url);
    const channelId = this.getPhenixChannelId(url);
    const subscriberStreamTags = this.props.siteId ?
      [`siteId:${this.props.siteId}`, `channelId:${this.props.channelId}`] :
      [];

    const joinChannelCallback: IJoinChannelCallback = (err, response) => {
      const success = !err && response.status === 'ok';
      if (!success) {
        const error = err || new Error(`Response status: ${response.status}`);
        if (this.props.onError) { this.props.onError(error); }
      }
    };

    const subscriberCallback: ISubscriberCallback = (err, response) => {
      const success = !err && ['ok', 'no-stream-playing'].includes(response.status);
      if (!success) {
        const error = err || new Error(`Response status: ${response.status}`);
        if (this.props.onError) { this.props.onError(error); }
      }
      // otherwise, response.mediaStream.getStreamId() will be a thing
    };

    getPhenixSdk().then((phenix) => {
      if (this.channelExpress) {
        this.channelExpress.dispose();
        this.channelExpress = null;
      }

      const adminApiProxyClient = new phenix.net.AdminApiProxyClient();
      adminApiProxyClient.setBackendUri(backendUri);
      adminApiProxyClient.setEndpointPaths({
        createAuthTokenPath: '/auth',
        createStreamTokenPath: '/stream',
      });

      this.channelExpress = new phenix.express.ChannelExpress({
        adminApiProxyClient,
        // @ts-ignore
        disableConsoleLogging: !this.props.enableLogging,
      });

      this.channelExpress.onError = (error: Error) => {
        // tslint:disable no-console
        console.error('Phenix player error:');
        console.error(error);
        // tslint:enable no-console
      };

      if (this.player && this.player.player && this.channelExpress) {
        this.channelExpress.joinChannel(
          {
            channelId,
            subscriberOptions: {
              tags: subscriberStreamTags,
            },
            videoElement: this.player.player,
          },
          joinChannelCallback,
          subscriberCallback,
        );
      }
    });
  }
  mute = () => {
    if (!this.player) { return; }
    this.player.mute();
  }
  pause() {
    if (!this.player) { return; }
    this.player.pause();
  }
  play() {
    if (!this.player) { return; }
    this.player.play();
  }

  ref = (player: FilePlayer) => {
    if (player === this.player) {
      return;
    }
    this.player = player;
  }

  render() {
    return (
      <FilePlayer
        {...this.props}
        url={undefined}
        ref={this.ref}
      />
    );
  }
  seekTo(seconds: number) {
    if (seconds === Infinity || this.getDuration() === Infinity) {
      return;
    }
    if (!this.player) { return; }
    this.player.seekTo(seconds);
  }
  setPlaybackRate(rate: number) {
    if (!this.player) { return; }
    this.player.setPlaybackRate(rate);
  }
  setVolume(fraction: number) {
    if (!this.player) { return; }
    this.player.setVolume(fraction);
  }
  stop() {
    if (this.channelExpress) {
      this.channelExpress.dispose();
      this.channelExpress = null;
    }
    if (this.player) { this.player.stop(); }
  }
  unmute = () => {
    if (!this.player) { return; }
    this.player.unmute();
  }
}
